import type { Breadcrumb } from '@sentry/core'
import type { CapturedNetworkRequest } from 'posthog-js'

// A download link from these hosts opens the customer file to anyone who holds it.
// The Google hosts carry Drive file ids and the sign-in and revoke calls for the Drive token.
const CLOUD_PROVIDER_HOSTS = [
  'dropboxusercontent.com',
  'sharepoint.com',
  '1drv.com',
  'api.box.com',
  'boxcloud.com',
  'www.googleapis.com',
  'content.googleapis.com',
  'oauth2.googleapis.com',
  'accounts.google.com',
] as const

const BREADCRUMB_URL_FIELDS = ['url', 'to', 'from'] as const

const isProviderHostname = (hostname: string) =>
  CLOUD_PROVIDER_HOSTS.some(
    (host) => hostname === host || hostname.endsWith(`.${host}`),
  )

// A relative or broken URL cannot point at a provider host, so it is kept.
// Not URL.canParse: Safari 16 and older do not have it.
const hostnameIfAbsolute = (url: string) => {
  try {
    return new URL(url).hostname
  } catch {
    return null
  }
}

export const isCloudProviderUrl = (url: unknown) => {
  if (typeof url !== 'string') return false
  const hostname = hostnameIfAbsolute(url)
  return hostname !== null && isProviderHostname(hostname)
}

export const dropCloudProviderBreadcrumb = (breadcrumb: Breadcrumb) =>
  BREADCRUMB_URL_FIELDS.some((field) =>
    isCloudProviderUrl(breadcrumb.data?.[field]),
  )
    ? null
    : breadcrumb

export const dropCloudProviderRequest = (request: CapturedNetworkRequest) =>
  isCloudProviderUrl(request.name) ? null : request
